import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Status_Enum,
  type GetMyItemsDashboardQuery,
  Issue_Assessment_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isActionStatusOverdue } from '@/components/action-status-badge/utils';
import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';

import { useGetTypes } from './config';

export type RibbonItem = {
  Id: string;
  Type: string;
  StatusLabelled: string;
};

/**
 * Flattens all entity arrays from the My Items dashboard query into a single
 * RibbonItem list, resolving StatusLabelled for each entity type from the
 * appropriate rating scale or common lookup.
 *
 */
export const useFlattenRibbonData = (
  data: GetMyItemsDashboardQuery | undefined
): RibbonItem[] => {
  const { t } = useTranslation('common');
  const { getByValue: getCommonLookupByValue } = useCommonLookupLazy();
  const actionRatings = useRating('action_status');
  const assessmentRatings = useRating('assessment_status');
  const activityRatings = useRating('assessment_activity_status');
  const issueRatings = useRating('issue_assessment_status');
  const types = useGetTypes();

  return useMemo(() => {
    if (!data) {
      return [];
    }

    const activeLabel = t('active');

    const risks: RibbonItem[] = (data.risk ?? []).map((d) => ({
      Id: d.Id,
      Type: types.risk,
      StatusLabelled:
        getCommonLookupByValue('statuses', d.Status ?? undefined)?.label ??
        activeLabel,
    }));

    const actions: RibbonItem[] = (data.action ?? []).map((d) => ({
      Id: d.Id,
      Type: types.action,
      // Mirror the Action Register's label derivation (pages/actions/useLabelledFields.tsx):
      // Open/Pending + DateDue in the past → "Overdue", otherwise the raw status label.
      // Keeping the two in lockstep means ribbon filters and destination-register filters
      // agree on which rows a given StatusLabelled value matches.
      StatusLabelled: isActionStatusOverdue({
        item: { Status: d.Status, DateDue: d.DateDue },
      })
        ? (actionRatings.getByValue('overdue')?.label ??
          actionRatings.getLabel('overdue'))
        : actionRatings.getLabel(d.Status),
    }));

    const assessments: RibbonItem[] = (data.assessment ?? []).map((d) => ({
      Id: d.Id,
      Type: types.assessment,
      StatusLabelled: assessmentRatings.getLabel(
        d.Status ?? Assessment_Status_Enum.Notstarted
      ),
    }));

    const controls: RibbonItem[] = (data.control ?? []).map((d) => ({
      Id: d.Id,
      Type: types.control,
      StatusLabelled: activeLabel,
    }));

    const issues: RibbonItem[] = (data.issue ?? []).map((d) => ({
      Id: d.Id,
      Type: types.issue,
      StatusLabelled: issueRatings.getLabel(
        d.assessment?.Status ?? Issue_Assessment_Status_Enum.Open
      ),
    }));

    const activities: RibbonItem[] = (data.assessment_activity ?? []).map(
      (d) => ({
        Id: d.Id,
        Type: types.RCSAActivity,
        StatusLabelled: activityRatings.getLabel(
          d.Status ?? Assessment_Activity_Status_Enum.Inprogress
        ),
      })
    );

    const documents: RibbonItem[] = (data.document ?? []).map((d) => ({
      Id: d.Id,
      Type: types.document,
      StatusLabelled: activeLabel,
    }));

    const indicators: RibbonItem[] = (data.indicator ?? []).map((d) => ({
      Id: d.Id,
      Type: types.indicator,
      StatusLabelled: activeLabel,
    }));

    const obligations: RibbonItem[] = (data.obligation ?? []).map((d) => ({
      Id: d.Id,
      Type: types.obligationReview,
      StatusLabelled: activeLabel,
    }));

    return [
      ...risks,
      ...actions,
      ...assessments,
      ...controls,
      ...issues,
      ...activities,
      ...documents,
      ...indicators,
      ...obligations,
    ];
  }, [
    data,
    t,
    types,
    getCommonLookupByValue,
    actionRatings,
    assessmentRatings,
    activityRatings,
    issueRatings,
  ]);
};
